import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  normalizeKeyword,
  normalizeKeywords,
} from "../../../utils/searchKeywords";

type Mode = "tags" | "bulk" | "list";

interface SearchKeywordsEditorProps {
  value: string[];
  onChange: (keywords: string[]) => void;
  label?: string;
  placeholder?: string;
  bulkPlaceholder?: string;
}

export const SearchKeywordsEditor = ({
  value,
  onChange,
  label,
  placeholder = "Type keyword and press Enter or comma",
  bulkPlaceholder = "Enter keywords separated by commas",
}: SearchKeywordsEditorProps) => {
  const primaryColor = "#f58220";
  const accentColor = "#15bfd5";
  const scrollAreaSx = {
    maxHeight: "220px",
    overflowY: "auto",
    pr: 0.5,
  };
  const brandedTextFieldSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: accentColor,
      },
      "&:hover fieldset": {
        borderColor: accentColor,
      },
      "&.Mui-focused fieldset": {
        borderColor: primaryColor,
        borderWidth: "2px",
      },
    },
  };

  const [mode, setMode] = useState<Mode>("bulk");
  const [draft, setDraft] = useState("");
  const [bulkInput, setBulkInput] = useState("");
  const [bulkDirty, setBulkDirty] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const normalizedValue = useMemo(
    () => normalizeKeywords(value || []),
    [value],
  );

  useEffect(() => {
    if (!bulkDirty) {
      setBulkInput(normalizedValue.join(", "));
    }
  }, [normalizedValue, bulkDirty]);

  useEffect(() => {
    if (mode === "bulk") {
      setBulkInput(normalizedValue.join(", "));
      setBulkDirty(false);
    }
  }, [mode, normalizedValue]);

  const commit = (next: string[]) => onChange(normalizeKeywords(next));

  const addKeyword = (rawValue: string) => {
    const keyword = normalizeKeyword(rawValue);
    if (!keyword) return;
    commit([...normalizedValue, keyword]);
  };

  const removeKeyword = (index: number) => {
    commit(normalizedValue.filter((_, currentIndex) => currentIndex !== index));
  };

  const startEdit = (index: number) => {
    setEditingIndex(index);
    setEditingValue(normalizedValue[index] || "");
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    const next = [...normalizedValue];
    next[editingIndex] = editingValue;
    commit(next);
    setEditingIndex(null);
    setEditingValue("");
  };

  const handleDraftKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addKeyword(draft);
      setDraft("");
    }
  };

  const handleListEditKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      saveEdit();
    }
  };

  const applyBulkInput = () => {
    const parsed = normalizeKeywords(bulkInput.split(","));
    commit(parsed);
    setBulkDirty(false);
  };

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {label && (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2" fontWeight={600}>
            {label}
          </Typography>
          <Tooltip
            title="Manage search keywords in three ways. Use Bulk for comma-separated editing, Quick Add for fast one-by-one entry, and List for row-based edit/delete."
            placement="top"
          >
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ color: "#9e9e9e", cursor: "help" }}
            />
          </Tooltip>
        </Box>
      )}

      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_, nextMode: Mode | null) => {
          if (nextMode) setMode(nextMode);
        }}
        sx={{
          "& .MuiToggleButton-root": {
            borderColor: accentColor,
            color: accentColor,
            textTransform: "none",
            fontWeight: 600,
          },
          "& .MuiToggleButton-root.Mui-selected": {
            color: "#fff",
            backgroundColor: primaryColor,
            borderColor: primaryColor,
          },
          "& .MuiToggleButton-root.Mui-selected:hover": {
            backgroundColor: primaryColor,
          },
        }}
      >
        <ToggleButton value="bulk">
          <Box display="flex" alignItems="center" gap={0.5}>
            <span>Bulk</span>
            <Tooltip
              title="Enter all keywords in one field using commas, then click Apply to replace the current keyword list."
              placement="top"
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{
                  color: mode === "bulk" ? "#ffffff" : "#9e9e9e",
                  cursor: "help",
                }}
              />
            </Tooltip>
          </Box>
        </ToggleButton>
        <ToggleButton value="tags">
          <Box display="flex" alignItems="center" gap={0.5}>
            <span>Quick Add</span>
            <Tooltip
              title="Type one keyword at a time and press Enter or comma to add it instantly."
              placement="top"
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{
                  color: mode === "tags" ? "#ffffff" : "#9e9e9e",
                  cursor: "help",
                }}
              />
            </Tooltip>
          </Box>
        </ToggleButton>
        <ToggleButton value="list">
          <Box display="flex" alignItems="center" gap={0.5}>
            <span>List</span>
            <Tooltip
              title="Review keywords as rows. Use edit/delete actions for each item, then add new keywords from the field below."
              placement="top"
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{
                  color: mode === "list" ? "#ffffff" : "#9e9e9e",
                  cursor: "help",
                }}
              />
            </Tooltip>
          </Box>
        </ToggleButton>
      </ToggleButtonGroup>

      {mode === "bulk" && (
        <>
          <TextField
            value={bulkInput}
            multiline
            minRows={4}
            onChange={(event) => {
              setBulkInput(event.target.value);
              setBulkDirty(true);
            }}
            placeholder={bulkPlaceholder}
            fullWidth
            sx={brandedTextFieldSx}
          />
          <Box display="flex" gap={1} alignItems="center">
            <Button
              variant="contained"
              size="small"
              onClick={applyBulkInput}
              disabled={!bulkDirty}
              sx={{
                backgroundColor: primaryColor,
                "&:hover": { backgroundColor: primaryColor },
              }}
            >
              Apply
            </Button>
            <Typography variant="caption" color="text.secondary">
              {normalizedValue.length} keywords
            </Typography>
          </Box>
        </>
      )}

      {mode === "tags" && (
        <>
          <TextField
            value={draft}
            size="small"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleDraftKeyDown}
            placeholder={placeholder}
            fullWidth
            sx={brandedTextFieldSx}
          />
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={scrollAreaSx}
          >
            {!normalizedValue.length && (
              <Typography variant="caption" color="text.secondary">
                No keywords yet. Add one using Quick Add or Bulk.
              </Typography>
            )}
            {normalizedValue.map((keyword, index) => (
              <Chip
                key={`${keyword}-${index}`}
                label={keyword}
                onDelete={() => removeKeyword(index)}
                sx={{
                  backgroundColor: "#e8fbfd",
                  border: `1px solid ${accentColor}`,
                  color: "#0d6672",
                }}
              />
            ))}
          </Stack>
        </>
      )}

      {mode === "list" && (
        <Stack spacing={1}>
          <Box sx={scrollAreaSx}>
            <Stack spacing={1}>
              {normalizedValue.map((keyword, index) => (
                <Box key={`${keyword}-${index}`} display="flex" gap={1}>
                  {editingIndex === index ? (
                    <TextField
                      value={editingValue}
                      size="small"
                      onChange={(event) => setEditingValue(event.target.value)}
                      onKeyDown={handleListEditKeyDown}
                      fullWidth
                      sx={brandedTextFieldSx}
                    />
                  ) : (
                    <TextField
                      value={keyword}
                      size="small"
                      fullWidth
                      disabled
                      sx={brandedTextFieldSx}
                    />
                  )}
                  {editingIndex === index ? (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={saveEdit}
                      sx={{
                        backgroundColor: primaryColor,
                        "&:hover": { backgroundColor: primaryColor },
                      }}
                    >
                      Save
                    </Button>
                  ) : (
                    <IconButton size="small" onClick={() => startEdit(index)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => removeKeyword(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              {!normalizedValue.length && (
                <Typography variant="caption" color="text.secondary">
                  No keywords yet. Add your first keyword below.
                </Typography>
              )}
            </Stack>
          </Box>

          <Box display="flex" gap={1} mt={0.5}>
            <TextField
              size="small"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleDraftKeyDown}
              placeholder="Add keyword"
              fullWidth
              sx={brandedTextFieldSx}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={!normalizeKeyword(draft)}
              sx={{
                borderColor: accentColor,
                color: accentColor,
                "&:hover": {
                  borderColor: accentColor,
                  backgroundColor: "#e8fbfd",
                },
              }}
              onClick={() => {
                addKeyword(draft);
                setDraft("");
              }}
            >
              Add
            </Button>
          </Box>
        </Stack>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {normalizedValue.length} total
        </Typography>
        <Button
          size="small"
          color="inherit"
          onClick={() => onChange([])}
          sx={{ color: primaryColor, fontWeight: 600 }}
        >
          Clear all
        </Button>
      </Box>
    </Box>
  );
};
